import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getToken } from "../utils/auth";
import { GalleryAPI } from "../utils/api";
import toast from "react-hot-toast";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import ConfirmModal from "../components/ConfirmModal";

interface GalleryPhoto {
    id: string;
    url: string;
    title?: string;
    description?: string;
    uploaderName: string;
    uploaded_by: string;
    className?: string;
    status: "pending" | "approved" | "rejected";
    createdAt: string;
    fileSize: number;
}

const GalleryPage: React.FC = () => {
    const [photos, setPhotos] = useState<GalleryPhoto[]>([]);
    const [loading, setLoading] = useState(true);
    const [lightboxIndex, setLightboxIndex] = useState(-1);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploadTitle, setUploadTitle] = useState("");
    const [uploadDescription, setUploadDescription] = useState("");
    const [uploading, setUploading] = useState(false);
    const [quota, setQuota] = useState<{ used: number; limit: number; percentage: number } | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [currentUserId, setCurrentUserId] = useState<string | null>(null);
    const [showPending, setShowPending] = useState(false);
    const [confirmModal, setConfirmModal] = useState<{
        isOpen: boolean;
        title: string;
        message: React.ReactNode;
        type: 'danger' | 'info' | 'success';
        onConfirm: () => void;
        confirmText?: string;
    }>({ isOpen: false, title: '', message: '', type: 'info', onConfirm: () => { } });
    const navigate = useNavigate();

    const closeConfirmModal = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

    useEffect(() => {
        loadPhotos();
        loadQuota();
        checkAdminStatus();
    }, [showPending]); // eslint-disable-line react-hooks/exhaustive-deps

    const checkAdminStatus = async () => {
        try {
            const token = getToken();
            if (!token) return;

            const { AuthAPI } = await import("../utils/api");
            const userRes = await AuthAPI.me(token);
            setIsAdmin(userRes.user.role === "admin");
            setCurrentUserId(userRes.user.id);
        } catch (error) {
            console.error("Failed to check admin status:", error);
        }
    };

    const loadPhotos = async () => {
        try {
            setLoading(true);
            const token = getToken();
            if (!token) {
                navigate("/login");
                return;
            }

            const status = showPending ? "pending" : "approved";
            const data = await GalleryAPI.list({ status, limit: 100 }, token);
            setPhotos(data.photos);
        } catch (error) {
            console.error("Failed to load photos:", error);
            toast.error("Không thể tải ảnh");
        } finally {
            setLoading(false);
        }
    };

    const loadQuota = async () => {
        try {
            const token = getToken();
            if (!token) return;

            const quotaData = await GalleryAPI.getQuota(token);
            setQuota(quotaData);
        } catch (error) {
            console.error("Failed to load quota:", error);
        }
    };

    const handleUpload = async () => {
        if (!uploadFile) {
            toast.error("Vui lòng chọn ảnh");
            return;
        }

        try {
            setUploading(true);
            const token = getToken();
            if (!token) return;

            await GalleryAPI.upload(uploadFile, { title: uploadTitle, description: uploadDescription }, token);
            toast.success("Upload thành công! Ảnh đang chờ admin duyệt");
            setShowUploadModal(false);
            setUploadFile(null);
            setUploadTitle("");
            setUploadDescription("");
            loadPhotos();
            loadQuota();
        } catch (error: any) {
            console.error("Upload failed:", error);
            toast.error(error.message || "Upload thất bại");
        } finally {
            setUploading(false);
        }
    };

    const handleApprove = async (photoId: string) => {
        try {
            const token = getToken();
            if (!token) return;

            await GalleryAPI.approve(photoId, token);
            toast.success("Đã duyệt ảnh");

            // Remove from current view if in pending mode
            if (showPending) {
                setPhotos(prev => prev.filter(p => p.id !== photoId));
            }
            setLightboxIndex(-1);
        } catch (error) {
            toast.error("Không thể duyệt ảnh");
        }
    };

    const handleReject = (photoId: string) => {
        const photo = photos.find(p => p.id === photoId);

        setConfirmModal({
            isOpen: true,
            title: 'Từ chối ảnh',
            message: `Bạn có chắc muốn từ chối và xóa ảnh "${photo?.title || 'Không có tiêu đề'}"?`,
            type: 'danger',
            confirmText: 'Từ chối',
            onConfirm: async () => {
                try {
                    const token = getToken();
                    if (!token) return;

                    await GalleryAPI.reject(photoId, token);
                    toast.success("Đã từ chối và xóa ảnh");

                    setPhotos(prev => prev.filter(p => p.id !== photoId));
                    setLightboxIndex(-1);
                    loadQuota();
                    closeConfirmModal();
                } catch (error) {
                    toast.error("Không thể từ chối ảnh");
                }
            }
        });
    };

    const handleDelete = (photoId: string) => {
        const photo = photos.find(p => p.id === photoId);

        // Check if user has permission to delete
        if (!isAdmin && photo?.uploaded_by !== currentUserId) {
            toast.error("Bạn không thể xóa ảnh của người khác");
            return;
        }

        setConfirmModal({
            isOpen: true,
            title: 'Xóa ảnh',
            message: `Bạn có chắc muốn xóa ảnh "${photo?.title || 'Không có tiêu đề'}"?`,
            type: 'danger',
            confirmText: 'Xóa',
            onConfirm: async () => {
                try {
                    const token = getToken();
                    if (!token) return;

                    await GalleryAPI.remove(photoId, token);
                    toast.success("Đã xóa ảnh");

                    setPhotos(prev => prev.filter(p => p.id !== photoId));
                    setLightboxIndex(-1);
                    loadQuota();
                    closeConfirmModal();
                } catch (error) {
                    toast.error("Không thể xóa ảnh");
                }
            }
        });
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return bytes + " B";
        if (bytes < 1048576) return (bytes / 1024).toFixed(2) + " KB";
        return (bytes / 1048576).toFixed(2) + " MB";
    };

    return (
        <>
            {/* Hero Section - Full Width */}
            <div className="relative overflow-hidden group bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 dark:from-blue-900 dark:via-slate-900 dark:to-slate-950 shadow-2xl animate-slideDownIn">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl mix-blend-overlay"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl mix-blend-overlay"></div>

                {/* Overlay pattern */}
                <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_1px_1px,_#fff_1px,_transparent_0)] bg-[size:24px_24px] pointer-events-none"></div>

                {/* Shimmer Effect */}
                <div className="absolute inset-0 opacity-20 bg-gradient-to-r from-transparent via-white/40 to-transparent blur-[2px] animate-[shimmer_3s_ease-in-out_infinite] [mask-image:linear-gradient(to_right,transparent_0%,black_20%,black_80%,transparent_100%)] mix-blend-overlay pointer-events-none"></div>

                <div className="max-w-screen-2xl mx-auto p-8 sm:p-12">
                    <div className="relative z-10">
                        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-mono font-bold text-white mb-4 tracking-tight">
                            Ảnh kỉ niệm
                        </h1>
                        <p className="text-base font-mono sm:text-lg text-blue-100 dark:text-blue-200 max-w-2xl leading-relaxed">
                            Lưu giữ những khoảnh khắc đáng nhớ của lớp
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 animate-fadeIn" translate="no">

                {/* Actions Card */}
                <div className="mb-6 card p-4 sm:p-6 animate-slideUpIn">
                    {/* Quota Bar */}
                    {quota && (
                        <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Dung lượng đã sử dụng
                                </span>
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {formatFileSize(quota.used)} / {formatFileSize(quota.limit)} ({quota.percentage.toFixed(1)}%)
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div
                                    className={`h-2 rounded-full transition-all ${quota.percentage > 90
                                        ? "bg-red-500"
                                        : quota.percentage > 70
                                            ? "bg-yellow-500"
                                            : "bg-primary-500"
                                        }`}
                                    style={{ width: `${Math.min(quota.percentage, 100)}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Upload Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={() => setShowUploadModal(true)}
                            className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Tải ảnh lên
                        </button>

                        {isAdmin && (
                            <button
                                onClick={() => setShowPending(!showPending)}
                                className={`btn-secondary flex items-center justify-center gap-2 w-full sm:w-auto ${showPending ? "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300" : ""
                                    }`}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {showPending ? "Hiện ảnh đã duyệt" : "Hiện ảnh chờ duyệt"}
                            </button>
                        )}
                    </div>
                </div>

                {/* Photo Grid */}
                {loading ? (
                    <div className="py-16 flex items-center justify-center">
                        {(() => {
                            const SpinnerLoading = require("../components/SpinnerLoading").default;
                            return <div style={{ transform: 'scale(0.435)' }}><SpinnerLoading /></div>;
                        })()}
                    </div>
                ) : photos.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            Chưa có ảnh nào
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                            Hãy là người đầu tiên upload ảnh kỉ niệm!
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {photos.map((photo, index) => (
                            <div
                                key={photo.id}
                                className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer bg-gray-100 dark:bg-gray-800 hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 animate-slideUpIn"
                                style={{ animationDelay: `${(index % 12) * 0.05}s` }}
                            >
                                <img
                                    src={photo.url}
                                    alt={photo.title || "Gallery photo"}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                    onClick={() => setLightboxIndex(index)}
                                />
                                {photo.status === "pending" && (
                                    <div className="absolute top-2 right-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded-full shadow-lg z-10">
                                        Chờ duyệt
                                    </div>
                                )}

                                {/* Delete button (for owner or admin) - Desktop only */}
                                {(isAdmin || photo.uploaded_by === currentUserId) && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(photo.id);
                                        }}
                                        className="hidden sm:block absolute top-2 left-2 bg-red-500/90 hover:bg-red-600 active:bg-red-700 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 shadow-lg backdrop-blur-sm"
                                        title="Xóa ảnh"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                )}

                                <div
                                    className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                    onClick={() => setLightboxIndex(index)}
                                >
                                    <div className="absolute bottom-0 left-0 right-0 p-3">
                                        <p className="text-white font-medium text-sm truncate">
                                            {photo.title || "Không có tiêu đề"}
                                        </p>
                                        <p className="text-white/80 text-xs">{photo.uploaderName}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Upload Modal */}
                {showUploadModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-md w-full p-6">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                                Upload ảnh mới
                            </h2>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Chọn ảnh (max 2MB)
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                        onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                                        className="w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 dark:file:bg-primary-900/30 dark:file:text-primary-300"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Tiêu đề (tùy chọn)
                                    </label>
                                    <input
                                        type="text"
                                        value={uploadTitle}
                                        onChange={(e) => setUploadTitle(e.target.value)}
                                        className="input-field"
                                        placeholder="Ví dụ: Chuyến dã ngoại 2024"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Mô tả (tùy chọn)
                                    </label>
                                    <textarea
                                        value={uploadDescription}
                                        onChange={(e) => setUploadDescription(e.target.value)}
                                        className="input-field"
                                        rows={3}
                                        placeholder="Thêm mô tả cho ảnh..."
                                    />
                                </div>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={handleUpload}
                                    disabled={uploading || !uploadFile}
                                    className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {uploading ? "Đang upload..." : "Upload"}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowUploadModal(false);
                                        setUploadFile(null);
                                        setUploadTitle("");
                                        setUploadDescription("");
                                    }}
                                    className="btn-secondary"
                                    disabled={uploading}
                                >
                                    Hủy
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Lightbox */}
                <Lightbox
                    open={lightboxIndex >= 0}
                    close={() => setLightboxIndex(-1)}
                    index={lightboxIndex}
                    slides={photos.map(photo => ({
                        src: photo.url,
                        alt: photo.title || "Gallery photo",
                        title: photo.title,
                        description: `Đăng bởi ${photo.uploaderName} • ${photo.createdAt}${photo.description ? '\n' + photo.description : ''}`
                    }))}
                    toolbar={{
                        buttons: [
                            ...(lightboxIndex >= 0 && photos[lightboxIndex] ? [
                                // Admin approve/reject buttons for pending photos
                                ...(isAdmin && photos[lightboxIndex].status === "pending" ? [
                                    <button
                                        key="approve"
                                        type="button"
                                        className="yarl__button"
                                        onClick={() => handleApprove(photos[lightboxIndex].id)}
                                        title="Duyệt ảnh"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </button>,
                                    <button
                                        key="reject"
                                        type="button"
                                        className="yarl__button"
                                        onClick={() => handleReject(photos[lightboxIndex].id)}
                                        title="Từ chối"
                                        style={{ color: '#ef4444' }}
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                ] : []),
                                // Delete button for admin or approved photos
                                ...(isAdmin || photos[lightboxIndex].status === "approved" ? [
                                    <button
                                        key="delete"
                                        type="button"
                                        className="yarl__button"
                                        onClick={() => handleDelete(photos[lightboxIndex].id)}
                                        title="Xóa ảnh"
                                        style={{ color: '#ef4444' }}
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                ] : [])
                            ] : []),
                            "close"
                        ]
                    }}
                    styles={{
                        container: { backgroundColor: "rgba(0, 0, 0, 0.95)" }
                    }}
                />

                {/* Confirm Modal */}
                <ConfirmModal
                    isOpen={confirmModal.isOpen}
                    onClose={closeConfirmModal}
                    onConfirm={confirmModal.onConfirm}
                    title={confirmModal.title}
                    message={confirmModal.message}
                    type={confirmModal.type}
                    confirmText={confirmModal.confirmText}
                />
            </div>
        </>
    );
};

export default GalleryPage;
